import { FC, useMemo, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import {
  HeroConversationMessage,
  CalendarInviteMessageData,
} from '@vs-online-only-app/state';
import { InsertInvitationOutlined } from '@mui/icons-material';
import { Stack, Typography, ButtonBase } from '@mui/material';
import ParticipantMessage from './ParticipantMessage';
import { CalendarEvent, buildCalendarUrl, CalendarType } from '@vs-online-only-app/ui';
import CalendarInviteModal from './CalendarInviteModal';

const Wrapper = styled(ButtonBase)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow: hidden;
  max-width: 290px;
  position: relative;
  text-align: left;
`;

const IconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  height: 50px;
  width: 50px;
  background-color: ${({ theme }) => theme.palette.action.hover};
  overflow: hidden;
`;

const OverflowText = styled(Typography)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 185px;
`;

type Props = {
  message: HeroConversationMessage;
};

const CalendarInviteMessage: FC<Props> = ({ message }) => {
  const invite = message.attributes.data as CalendarInviteMessageData;
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const startDate = useMemo(() => new Date(invite.invitationStartDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }), [invite.invitationStartDate]);

  const endDate = useMemo(() => new Date(invite.invitationEndDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }), [invite.invitationEndDate]);

  const title = invite.title || invite.placeName || 'Calendar Invite';

  const [appleUrl, googleUrl, outlookUrl, outlookWebUrl] = useMemo(() => {
    if (!invite?.invitationStartDate) return ['', '', '', ''];
    const calendarEvent: CalendarEvent = {
      allDay: !!(invite.invitationStartDate && !invite.invitationEndDate),
      start: new Date(invite.invitationStartDate),
      ...(invite?.invitationEndDate && { end: new Date(invite.invitationEndDate) }),
      title,
      description: invite.placeName,
      location: invite.address,
      coordinates:
        invite.location ? `${invite.location.lat};${invite.location.lon}` : '',
    };

    return [
      buildCalendarUrl(calendarEvent, CalendarType.Apple),
      buildCalendarUrl(calendarEvent, CalendarType.Google),
      buildCalendarUrl(calendarEvent, CalendarType.Outlook),
      buildCalendarUrl(calendarEvent, CalendarType.OutlookWeb),
    ];
  }, [invite.invitationStartDate, invite.invitationEndDate, title, invite.placeName, invite.address, invite.location]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleMessageClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  return (
    <ParticipantMessage message={message}>
      <Wrapper
        ref={anchorRef}
        disabled={!appleUrl && !googleUrl && !outlookUrl && !outlookWebUrl}
        onClick={handleMessageClick}
      >
        <Stack gap={0.5}>
          <IconWrapper>
            <InsertInvitationOutlined fontSize="medium" />
          </IconWrapper>
        </Stack>
        <Stack gap={0.5}>
          <OverflowText
            variant='caption'
            fontWeight={500}
          >
            {title}
          </OverflowText>
          <OverflowText variant='caption'>
            {`${startDate} - ${endDate}`}
          </OverflowText>
        </Stack>
      </Wrapper>
      <CalendarInviteModal
        anchorRef={anchorRef}
        open={modalOpen}
        onClose={handleModalClose}
        appleUrl={appleUrl}
        googleUrl={googleUrl}
        outlookUrl={outlookUrl}
        outlookWebUrl={outlookWebUrl}
        onItemDownloaded={() => { console.log('downloaded'); }}
      />
    </ParticipantMessage>
  );
};

export default CalendarInviteMessage;
